import { useEffect, useMemo } from "react";

import loadingScreen from "@assets/page-loading.gif";
import { GenericTooltip } from "@components/tooltip-container";
import { useNorthbeamLazyQuery } from "@hooks/http";
import { type FetchClientSettingsForCycle } from "@north-beam/nb-client-side";
import { MMMClientState, type MMMClientCycle } from "@north-beam/nb-common";
import { CLIENT_SETTINGS_FOR_CYCLE_QUERY } from "@pages/mmm/explore-budgets-v2/explore-budgets-queries";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";

import { ClientSettingsContainer } from "./client-setting/client-settings-container";
import { type ClientSetting } from "./client-setting/types";

export function SettingsSection({
  clientCycle,
  onCreateNewClientSetting,
  onEditClientSetting,
}: {
  clientCycle: MMMClientCycle;
  onCreateNewClientSetting: () => void;
  onEditClientSetting: (clientSettingToEdit: ClientSetting) => void;
}) {
  const { clientSettings, setClientSettings } = useClientSettings();

  const {
    loadData: loadClientSettings,
    data: { loading: loadingClientSettings, data: clientSettingsData },
  } = useNorthbeamLazyQuery<FetchClientSettingsForCycle>(
    CLIENT_SETTINGS_FOR_CYCLE_QUERY,
  );

  const canAlterClientSettings = useMemo(() => {
    return [
      MMMClientState.CREATING_SETTINGS,
      MMMClientState.PREPARING_MIX,
      MMMClientState.ADOPTING_MIX,
      MMMClientState.TRACKING_MIX,
    ].includes(clientCycle.state);
  }, [clientCycle.state]);

  const canCreateClientSettings = useMemo(() => {
    return [
      MMMClientState.CREATING_SETTINGS,
      MMMClientState.PREPARING_MIX,
      MMMClientState.ADOPTING_MIX,
      MMMClientState.TRACKING_MIX,
    ].includes(clientCycle.state);
  }, [clientCycle.state]);

  const shouldHaveClientSettings = useMemo(() => {
    return [
      MMMClientState.ADOPTING_MIX,
      MMMClientState.PREPARING_MIX,
      MMMClientState.TRACKING_MIX,
      MMMClientState.REVIEWING_PERIOD,
    ].includes(clientCycle.state);
  }, [clientCycle.state]);

  const shouldQueryClientSettings = useMemo(() => {
    return (
      (clientCycle.id && shouldHaveClientSettings) || canCreateClientSettings
    );
  }, [clientCycle.id, shouldHaveClientSettings, canCreateClientSettings]);

  useEffect(() => {
    if (shouldQueryClientSettings) {
      loadClientSettings({
        variables: {
          id: clientCycle.id,
        },
      });
    } else if (!shouldQueryClientSettings) {
      setClientSettings([]);
    }
  }, [
    clientCycle.id,
    shouldQueryClientSettings,
    loadClientSettings,
    setClientSettings,
  ]);

  useEffect(() => {
    // TODO: Fix typing
    if (clientSettingsData) {
      setClientSettings(
        clientSettingsData.me.mmm.cycle
          .clientSettings as unknown as ClientSetting[],
      );
    }
  }, [clientSettingsData, setClientSettings]);

  if (loadingClientSettings) {
    return (
      <div className="flex-center">
        <img className="h-80" src={loadingScreen} alt="Loading screen" />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between">
        <div className="flex items-center mb-4">
          <span className="text-xl font-semibold">Select Your Settings</span>
          <GenericTooltip
            placement="right"
            className="h-7"
            hideArrow
            content="KPI, budget spend range, and spend requirements in your settings guide which budgets are recommended."
          />
        </div>
      </div>
      <ClientSettingsContainer
        canUpdate={canCreateClientSettings}
        canAlter={canAlterClientSettings}
        clientSettings={clientSettings}
        onCreateNewClientSetting={onCreateNewClientSetting}
        onEditClientSetting={onEditClientSetting}
      />
    </>
  );
}
